import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const clientAvatarArticle: BlogArticle = {
  key: BlogArticleKey.CLIENT_AVATAR_BUILD,
  slug: BlogSlugs.CLIENT_AVATAR_BUILD,
  categoryId: "03",
  date: "2026-07-25",
  readTime: 8,
  locales: {
    fr: {
      title: "Créer un avatar client à partir de vrais appels",
      excerpt:
        "La plupart des avatars clients sont des fictions écrites en réunion. Voici comment construire un avatar réel, à partir de ce que disent vraiment tes clients.",
      description:
        "Un avatar client inventé en réunion ne sert à rien. La méthode pour en construire un réel, extrait des mots de tes clients.",
      ctaTitle: "Construis ton avatar sur la vraie voix de tes clients",
      ctaText:
        "Les douleurs, les objections et les mots exacts de tes clients, réunis en un avatar que tu peux corriger et utiliser tout de suite. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tout le monde te dira de définir ton avatar client avant de vendre. Le conseil est juste. Le problème, c'est la façon dont on le fait : la plupart des avatars sont inventés. On s'assoit une heure, on imagine *Marie, 42 ans, directrice marketing, aime le yoga*, et on croit avoir compris son client.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un avatar inventé ne guide rien. Il rassure, il décore une présentation, mais il ne t'aide ni à écrire ton offre, ni à prospecter, ni à répondre aux objections. Pour ça, il faut un avatar réel, extrait de la vraie voix de tes clients.",
        },
        { type: ArticleBlockType.H2, text: "C'est quoi un avatar client, vraiment" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un avatar client, c'est le portrait précis de la personne qui achète vraiment chez toi. Pas une fiche démographique, mais un portrait opérationnel qui répond à des questions utiles :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Quelle est sa situation concrète quand il vient te voir ?",
            "Quelle douleur cherche-t-il vraiment à résoudre ?",
            "Qu'a-t-il déjà essayé avant, et pourquoi ça n'a pas marché ?",
            "Quelles objections a-t-il avant d'acheter ?",
            "Et surtout : avec quels mots exacts décrit-il tout ça ?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Bien construit, cet avatar devient la base de tout le reste : ton offre, ta prospection, ton discours, ton contenu. Mal construit, ou inventé, il t'envoie dans le mur en te donnant l'illusion de savoir.",
        },
        { type: ArticleBlockType.H2, text: "Persona inventé contre avatar réel" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence tient en un mot : la source. L'un part de ton imagination, l'autre de tes données réelles.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le persona inventé",
            items: [
              "Écrit en réunion, à partir de suppositions",
              "Détails démographiques inutiles (âge, hobbies)",
              "Décrit qui tu imagines, pas qui achète",
              "Reformulé avec ton vocabulaire d'expert",
              "Figé une fois pour toutes",
            ],
          },
          good: {
            title: "L'avatar réel",
            items: [
              "Extrait de tes appels et échanges réels",
              "Centré sur la douleur et la situation",
              "Décrit qui achète vraiment",
              "Formulé avec les mots exacts du client",
              "Affiné à chaque nouvel échange",
            ],
          },
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un avatar utile ne se devine pas. Il s'écoute. Tes clients te le dictent à chaque appel, si tu prends la matière au bon endroit.",
        },
        { type: ArticleBlockType.H2, text: "Construire son avatar client en 4 étapes" },
        { type: ArticleBlockType.H3, text: "1. Rassemble tes échanges réels" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Reprends tes derniers appels de vente, tes retours clients, tes messages. Tu n'as pas encore de clients ? Commence avec tes échanges de découverte, ou pose une première hypothèse que tu corrigeras vite. L'important est de partir de matière réelle dès que possible.",
        },
        { type: ArticleBlockType.H3, text: "2. Repère les douleurs et les mots récurrents" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Dans cette matière, note ce qui revient : les mêmes douleurs, les mêmes formulations, les mêmes objections. Ne filtre pas avec ton jugement d'expert. Ce qui compte, c'est ce qu'ils répètent, avec leurs mots, pas ce que tu crois important.",
        },
        { type: ArticleBlockType.H3, text: "3. Structure le portrait" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Assemble ces éléments en un portrait clair : situation de départ, douleur principale, ce qu'il a déjà essayé, ses objections, ses déclencheurs d'achat. Garde les verbatims, ces phrases exactes qui rendent l'avatar vivant et directement utilisable.",
        },
        { type: ArticleBlockType.H3, text: "4. Fais-le vivre" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un avatar n'est jamais fini. Chaque nouvel appel le précise, le corrige, l'enrichit. Au bout de quelques échanges, tu ne devines plus ta cible : tu la connais, avec ses mots. C'est là que l'avatar commence vraiment à te faire vendre.",
        },
        { type: ArticleBlockType.H2, text: "Ce que ça change concrètement" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un avatar réel n'est pas un exercice théorique. Il se répercute sur toute ta chaîne commerciale :",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Ton offre** répond à la douleur que tes clients citent le plus, formulée avec leurs mots.",
            "**Ta prospection** vise les profils qui vivent réellement cette situation, pas une case démographique.",
            "**Ton discours** anticipe les objections que tu as déjà entendues, au lieu de les découvrir en rendez-vous.",
            "**Ton contenu** parle de ce qui préoccupe vraiment ta cible, donc il résonne.",
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Extraire un avatar de tes appels à la main prend du temps, et on passe vite à côté des signaux qui reviennent. On analyse tes conversations clients, on en fait remonter les douleurs, les objections et les mots récurrents, et on construit un avatar réel qui s'enrichit à chaque échange. Cette même donnée sert ensuite à trouver tes prospects et à aligner ton discours. Une donnée captée une fois, réutilisée sur toute ta chaîne.",
          linkLabel: "Découvrir l'avatar client",
          linkPath: ProtectedPaths.CLIENT_AVATAR,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Arrête d'inventer ton client. Il te parle déjà, à chaque appel. Un bon avatar ne sort pas de ton imagination mais de tes échanges réels : leurs douleurs, leurs objections, leurs mots. Rassemble cette matière, repère ce qui revient, structure le portrait, et fais-le vivre. Tu passeras d'un persona qui décore tes slides à un avatar qui guide vraiment ta vente.",
        },
      ],
      faq: [
        {
          question: "C'est quoi un avatar client ?",
          answer:
            "Un avatar client est le portrait détaillé de ton client idéal : sa situation, ses douleurs, ses motivations, ses objections et les mots qu'il emploie pour en parler. Bien fait, il guide toute ta vente : ton offre, ta prospection, ton discours.",
        },
        {
          question: "Quelle différence entre un avatar client et un persona marketing ?",
          answer:
            "Un persona marketing est souvent une fiction démographique inventée (âge, poste, hobbies). Un avatar client utile part de données réelles : ce que tes vrais clients disent en appel, leurs douleurs concrètes et leurs mots exacts.",
        },
        {
          question: "Comment créer un avatar client quand on n'a pas encore de clients ?",
          answer:
            "Tu poses une première version à la main, basée sur ta meilleure hypothèse de cible. Puis tu l'affines dès tes premiers échanges réels : chaque appel corrige et précise l'avatar jusqu'à ce qu'il colle à la réalité.",
        },
        {
          question: "Où trouver la matière pour construire un avatar client ?",
          answer:
            "Dans tes appels de vente, tes retours clients, tes échanges et les objections récurrentes. C'est là que se trouvent les vraies douleurs et les mots exacts de tes clients, la matière première d'un avatar fidèle.",
        },
      ],
    },
    en: {
      title: "Create a client avatar from real calls (not an invented persona)",
      excerpt:
        "Most client avatars are fictions written in a meeting. Here is how to build a real avatar, from what your customers actually say.",
      description:
        "An invented client avatar is useless. The method to build a real avatar, drawn from your customers' own words, that actually guides your selling.",
      ctaTitle: "Build your avatar on what your customers actually say",
      ctaText:
        "Your customers' pains, objections and exact words, gathered into an avatar you can correct and use straight away. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Everyone will tell you to define your client avatar before you sell. The advice is right. The problem is how it is done: most avatars are invented. You sit down for an hour, you picture *Marie, 42, marketing director, likes yoga*, and you think you understand your customer.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An invented avatar guides nothing. It reassures, it decorates a slide deck, but it helps you neither write your offer, nor prospect, nor answer objections. For that, you need a real avatar, drawn from the true voice of your customers.",
        },
        { type: ArticleBlockType.H2, text: "What a client avatar really is" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A client avatar is the precise portrait of the person who actually buys from you. Not a demographic sheet, but an operational portrait that answers useful questions:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "What is their concrete situation when they come to you?",
            "What pain are they really trying to solve?",
            "What have they already tried, and why did it not work?",
            "What objections do they have before buying?",
            "And above all: what exact words do they use to describe all this?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Built well, this avatar becomes the foundation of everything else: your offer, your prospecting, your pitch, your content. Built badly, or invented, it sends you into the wall while giving you the illusion of knowing.",
        },
        { type: ArticleBlockType.H2, text: "Invented persona versus real avatar" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference comes down to one word: the source. One starts from your imagination, the other from your real data.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The invented persona",
            items: [
              "Written in a meeting, from assumptions",
              "Useless demographic details (age, hobbies)",
              "Describes who you imagine, not who buys",
              "Rephrased in your expert vocabulary",
              "Frozen once and for all",
            ],
          },
          good: {
            title: "The real avatar",
            items: [
              "Drawn from your real calls and exchanges",
              "Centred on the pain and the situation",
              "Describes who actually buys",
              "Worded in the customer's exact words",
              "Refined with every new exchange",
            ],
          },
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A useful avatar is not guessed. It is listened to. Your customers dictate it to you on every call, if you take the material from the right place.",
        },
        { type: ArticleBlockType.H2, text: "Building your client avatar in four steps" },
        { type: ArticleBlockType.H3, text: "1. Gather your real exchanges" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Go back through your last sales calls, your customer feedback, your messages. No customers yet? Start with your discovery conversations, or set a first hypothesis you will correct quickly. What matters is starting from real material as soon as possible.",
        },
        { type: ArticleBlockType.H3, text: "2. Spot the recurring pains and words" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In that material, note what comes back: the same pains, the same phrasings, the same objections. Do not filter with your expert judgment. What matters is what they repeat, in their words, not what you think is important.",
        },
        { type: ArticleBlockType.H3, text: "3. Structure the portrait" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Assemble these elements into a clear portrait: starting situation, main pain, what they already tried, their objections, their buying triggers. Keep the verbatims, those exact sentences that make the avatar alive and directly usable.",
        },
        { type: ArticleBlockType.H3, text: "4. Keep it alive" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An avatar is never finished. Every new call sharpens it, corrects it, enriches it. After a few exchanges, you no longer guess your target: you know it, in its own words. That is when the avatar truly starts to make you sell.",
        },
        { type: ArticleBlockType.H2, text: "What it changes in practice" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A real avatar is not a theoretical exercise. It ripples across your whole sales chain:",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Your offer** answers the pain your customers cite most, worded in their words.",
            "**Your prospecting** targets the profiles who really live that situation, not a demographic box.",
            "**Your pitch** anticipates the objections you have already heard, instead of discovering them in the meeting.",
            "**Your content** speaks to what really worries your target, so it resonates.",
          ],
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "Extracting an avatar from your calls by hand takes time, and you quickly miss the signals that keep coming back. We analyse your customer conversations, surface the pains, the objections and the recurring words, and build a real avatar that grows with every exchange. That same data then serves to find your prospects and align your pitch. Data captured once, reused across your whole chain.",
          linkLabel: "See the client avatar tool",
          linkPath: ProtectedPaths.CLIENT_AVATAR,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Stop inventing your customer. They are already talking to you, on every call. A good avatar does not come from your imagination but from your real exchanges: their pains, their objections, their words. Gather that material, spot what comes back, structure the portrait, and keep it alive. You will move from a persona that decorates your slides to an avatar that truly guides your selling.",
        },
      ],
      faq: [
        {
          question: "What is a client avatar?",
          answer:
            "A client avatar is the detailed portrait of your ideal customer: their situation, their pains, their motivations, their objections and the words they use to describe them. Done well, it guides your whole selling: your offer, your prospecting, your pitch.",
        },
        {
          question: "What is the difference between a client avatar and a marketing persona?",
          answer:
            "A marketing persona is often an invented demographic fiction (age, job, hobbies). A useful client avatar starts from real data: what your actual customers say on calls, their concrete pains and their exact words.",
        },
        {
          question: "How do I create a client avatar when I do not have customers yet?",
          answer:
            "You set a first version by hand, based on your best guess of your target. Then you refine it from your first real exchanges: each call corrects and sharpens the avatar until it matches reality.",
        },
        {
          question: "Where do I find the material to build a client avatar?",
          answer:
            "In your sales calls, your customer feedback, your exchanges and the recurring objections. That is where the real pains and the exact words of your customers sit, the raw material of a faithful avatar.",
        },
      ],
    },
  },
};
